import React, { useState } from 'react';
import { cn } from '../../utils';
import { Button } from '../ui/button';

interface JsonInputPanelProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isLoading?: boolean;
  error?: string | null;
  className?: string;
  placeholder?: string;
  submitLabel?: string;
}

export function JsonInputPanel({
  value,
  onChange,
  onSubmit,
  isLoading = false,
  error,
  className,
  placeholder = '{\n  "externalReference": "PO-12345",\n  "vendor": "ACME Corp",\n  "totalAmount": 5000.00\n}',
  submitLabel = 'Run Simulation'
}: JsonInputPanelProps) {
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleChange = (newValue: string) => {
    onChange(newValue);
    // Validate JSON as user types
    if (newValue.trim()) {
      try {
        JSON.parse(newValue);
        setJsonError(null);
      } catch (e) {
        setJsonError('Invalid JSON syntax');
      }
    } else {
      setJsonError(null);
    }
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(value);
      onChange(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (e) {
      setJsonError('Cannot format - invalid JSON');
    }
  };

  const handleClear = () => {
    onChange('');
    setJsonError(null);
  };

  const handleCopyToClipboard = async () => {
    if (!value.trim()) return;
    
    try {
      await navigator.clipboard.writeText(value);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const loadSampleData = () => {
    const sampleData = {
      orderHeader: {
        externalReference: "PO-2024-001234",
        orderDate: new Date().toISOString().split('T')[0],
        vendor: {
          id: "V-001",
          name: "ACME Corporation"
        },
        shipTo: {
          address: "123 Main Street",
          city: "Springfield",
          state: "IL",
          zip: "62701"
        }
      },
      lineItems: [
        {
          lineNumber: 1,
          productId: "SKU-001",
          description: "Widget A",
          quantity: 100,
          unitPrice: 25.00
        },
        {
          lineNumber: 2,
          productId: "SKU-002",
          description: "Widget B",
          quantity: 50,
          unitPrice: 45.00
        }
      ],
      totals: {
        subtotal: 4750.00,
        tax: 380.00,
        shipping: 120.00,
        total: 5250.00
      }
    };
    onChange(JSON.stringify(sampleData, null, 2));
    setJsonError(null);
  };

  const isValid = value.trim() && !jsonError;

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header with actions */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-secondary-700 dark:text-secondary-200">
          JSON Input Data
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadSampleData}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            Load Sample
          </button>
          <button
            type="button"
            onClick={handleCopyToClipboard}
            disabled={!value.trim()}
            className={cn(
              "text-xs font-medium disabled:opacity-50 transition-colors",
              copySuccess 
                ? "text-green-600 dark:text-green-400 hover:text-green-700" 
                : "text-secondary-600 dark:text-secondary-400 hover:text-secondary-700"
            )}
            title="Copy to clipboard"
          >
            {copySuccess ? (
              <>
                <i className="fa-solid fa-check mr-1"></i>
                Copied!
              </>
            ) : (
              <>
                <i className="fa-solid fa-copy mr-1"></i>
                Copy
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleFormat}
            disabled={!value.trim()}
            className="text-xs text-secondary-600 dark:text-secondary-400 hover:text-secondary-700 disabled:opacity-50"
          >
            Format
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={!value.trim()}
            className="text-xs text-secondary-600 dark:text-secondary-400 hover:text-secondary-700 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* JSON Input */}
      <div className="flex-1 relative">
        <textarea
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'w-full h-full min-h-[300px] p-4 font-mono text-sm',
            'border rounded-lg resize-none',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            jsonError
              ? 'border-red-300 bg-red-50 dark:bg-red-950'
              : 'border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800'
          )}
          spellCheck={false}
        />
        
        {/* JSON validation indicator */}
        {value.trim() && (
          <div className={cn(
            'absolute bottom-3 right-3 text-xs px-2 py-1 rounded',
            jsonError
              ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400'
              : 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400'
          )}>
            {jsonError ? '✗ Invalid' : '✓ Valid JSON'}
          </div>
        )}
      </div>

      {/* Error message */}
      {(jsonError || error) && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          {jsonError || error}
        </p>
      )}

      {/* Submit button */}
      <div className="mt-4">
        <Button
          onClick={onSubmit}
          disabled={!isValid || isLoading}
          className="w-full"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Processing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {submitLabel}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
